/**
 * One-shot wiring of the knowledge graph to its update sources.
 *
 * Sources today:
 *   - designStore (specs)   — subscribed; every store mutation re-syncs
 *                              the full spec list (cheap — N is small).
 *
 * Sources NOT wired here (handled inline elsewhere because they have
 * upstream pipelines that know more context):
 *   - AI file writes        — see AgentContext.tsx case 'file_modified'
 *                              which calls onFileChanged() directly.
 *   - Manual editor edits   — TODO: hook into useEditorState's
 *                              updateFileCode once that has a public
 *                              listener; until then the graph stays
 *                              one step behind editor-only edits.
 *
 * Call once at app start. Returns an unsubscribe function for tests.
 */

import { listSpecs, subscribe as subscribeDesignStore, getSpec as getDesignSpec } from '../design/designStore';
import { listAssets, subscribe as subscribeAssetStore } from '../assets/assetStore';
import {
  onSpecChanged, onSpecRemoved, onAssetChanged, onAssetRemoved, onApiProvided,
  getKnowledgeGraphSnapshot as getSnapshot,
} from '@gotti/sandbox-ai-v2';
import { mimeToAssetSubtype } from './assetSubtype';

let started = false;

export function initKnowledgeGraph(): () => void {
  if (started) return () => {};
  started = true;

  // Initial seed from whatever's already in the design store.
  for (const spec of listSpecs()) onSpecChanged(spec);

  // Diff-based sync on every change. Cheap because spec count is small
  // (< few dozen) and the KG's per-spec re-index is O(edges_per_spec).
  const knownIds = new Set(listSpecs().map(s => s.id));
  const unsubscribe = subscribeDesignStore(() => {
    const liveSpecs = listSpecs();
    const liveIds = new Set(liveSpecs.map(s => s.id));
    for (const s of liveSpecs) onSpecChanged(s);
    for (const id of knownIds) {
      if (!liveIds.has(id)) onSpecRemoved(id);
    }
    knownIds.clear();
    for (const id of liveIds) knownIds.add(id);
  });

  // Framework-injected client $api members (provided by the sandbox runtime's
  // createClientGame api(), not by any user system) — seed so their uses don't
  // falsely dangle.
  for (const name of ['canvas', 'ctx', 'container']) onApiProvided(name, 'client');

  // Assets: mirror the assetStore into the graph (path-keyed asset nodes).
  const knownAssetPaths = new Set<string>();
  const syncAssets = () => {
    const live = listAssets();
    const liveIds = new Set(live.map(a => a.path));
    // Pass `hash` so the KG can detect in-place swaps (same path, new bytes).
    // composite/specId are NOT carried on StoredAsset yet — wiring them would
    // require the materializer to stamp the store entry, which is out of scope
    // here (follow-up). Until then composite assets index as standalone nodes.
    for (const a of live) onAssetChanged({ path: a.path, subtype: mimeToAssetSubtype(a.mimeType), hash: a.hash });
    for (const path of knownAssetPaths) if (!liveIds.has(path)) onAssetRemoved(path);
    knownAssetPaths.clear();
    for (const p of liveIds) knownAssetPaths.add(p);
  };
  syncAssets(); // initial seed
  const unsubscribeAssets = subscribeAssetStore(syncAssets);

  return () => {
    unsubscribe();
    unsubscribeAssets();
    started = false;
  };
}

/** Test helper — returns current spec ids in the graph. */
export function _debugSpecIdsInGraph(): string[] {
  return getSnapshot().nodes.filter(n => n.kind === 'spec').map(n => n.id);
}

// Keep the unused-import lint quiet — getDesignSpec is here as a
// convenience re-export for callers debugging the wiring.
export { getDesignSpec };
